import React, {
  createContext,
  useState,
  useMemo,
  useContext,
  ReactNode,
  FC,
  useEffect,
  useCallback,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';

import { lightTheme, darkTheme } from './themes';

type Theme = 'light' | 'dark';

interface ThemeContextProps {
  toggleTheme: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

interface ThemeProviderWrapperProps {
  children: ReactNode;
}

/** Component that provides theme context to the application. */
export const ThemeProviderWrapper: FC<ThemeProviderWrapperProps> = ({
  children,
}) => {
  const isBrowser = typeof window !== 'undefined'; // Check if running in the browser
  const storedTheme = isBrowser
    ? (localStorage.getItem('theme') as Theme | null)
    : null;

  const [theme, setTheme] = useState<Theme>(storedTheme || 'light');

  useEffect(() => {
    if (isBrowser) {
      localStorage.setItem('theme', theme);
    }
  }, [theme, isBrowser]);

  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  }, []);

  const contextValue = useMemo(
    () => ({ toggleTheme, theme }),
    [toggleTheme, theme]
  );
  const themeMode = useMemo(
    () => (theme === 'light' ? lightTheme : darkTheme),
    [theme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={themeMode}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = (): ThemeContextProps =>
  useContext(ThemeContext)!;
